import { ApiError, del, get, post } from '../../api.js';

/**
 * Sending a video's offline copy (round 2): straight to Spaces, in parts of
 * 16 MB, three at a time, each to its own signed address. A part that
 * fails is tried again; without network the sending waits for it to come
 * back; a closed page resumes with the same file at the last part received.
 */
export interface OfflineUpload {
  id: string;
  file_name: string;
  size_bytes: number;
  part_bytes: number;
  parts_total: number;
  done: { number: number; size: number }[];
  created_at: string;
}

/** Why a sending stopped: no network (it goes on by itself when it is back) or stopped by the person. */
export class UploadStopped extends Error {
  constructor(readonly reason: 'offline' | 'stopped') {
    super(reason);
    this.name = 'UploadStopped';
  }
}

export interface SendOptions {
  onProgress(sent: number, total: number): void;
  signal?: AbortSignal;
  /** How a part is sent (tests replace it). */
  putPart?(
    url: string,
    part: Blob,
    onSent: (bytes: number) => void,
    signal?: AbortSignal,
  ): Promise<void>;
  wait?(ms: number): Promise<void>;
}

const PARALLEL = 3;
const TRIES = 5;

export const unfinishedUpload = (assetId: string) =>
  get<{ upload: OfflineUpload | null }>(`/content/${assetId}/offline/uploads`).then(
    (r) => r.upload,
  );

export async function sendOfflineCopy(assetId: string, file: File, options: SendOptions) {
  const base = `/content/${assetId}/offline/uploads`;
  const upload = await post<OfflineUpload>(base, { file_name: file.name, size: file.size });
  const received = new Set(upload.done.map((p) => p.number));
  const sending = new Map<number, number>();
  let finished = upload.done.reduce((n, p) => n + p.size, 0);
  const report = () =>
    options.onProgress(
      Math.min(file.size, finished + [...sending.values()].reduce((a, b) => a + b, 0)),
      file.size,
    );
  report();
  const todo = Array.from({ length: upload.parts_total }, (_, i) => i + 1).filter(
    (n) => !received.has(n),
  );
  const putPart = options.putPart ?? xhrPut;
  const wait = options.wait ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

  async function one(n: number) {
    const start = (n - 1) * upload.part_bytes;
    const part = file.slice(start, Math.min(file.size, start + upload.part_bytes));
    for (let attempt = 1; ; attempt += 1) {
      if (options.signal?.aborted) throw new UploadStopped('stopped');
      try {
        const { urls } = await post<{ urls: Record<string, string> }>(`${base}/${upload.id}/urls`, {
          parts: [n],
        });
        await putPart(
          urls[n]!,
          part,
          (bytes) => {
            sending.set(n, bytes);
            report();
          },
          options.signal,
        );
        sending.delete(n);
        received.add(n);
        finished += part.size;
        report();
        return;
      } catch (error) {
        sending.delete(n);
        report();
        if (error instanceof UploadStopped) throw error;
        const offline =
          (error instanceof ApiError && error.code === 'offline') ||
          (typeof navigator !== 'undefined' && !navigator.onLine);
        if (offline) throw new UploadStopped('offline');
        if (attempt >= TRIES) throw error;
        await wait(2000 * 2 ** (attempt - 1));
      }
    }
  }

  const queue = [...todo];
  await Promise.all(
    Array.from({ length: Math.min(PARALLEL, queue.length) }, async () => {
      for (let n = queue.shift(); n !== undefined; n = queue.shift()) await one(n);
    }),
  );
  // The portal asks the storage which parts it has, then joins them.
  await post(`${base}/${upload.id}/complete`, {});
}

/** Stops an unfinished upload: what was sent goes. */
export const cancelUpload = (assetId: string, uploadId: string) =>
  del(`/content/${assetId}/offline/uploads/${uploadId}`);

/** One part to its signed address (Spaces, or the API's stand-in), with its progress. */
function xhrPut(url: string, part: Blob, onSent: (bytes: number) => void, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => onSent(e.loaded);
    xhr.onerror = () => reject(new ApiError(0, 'offline'));
    xhr.onabort = () => reject(new UploadStopped('stopped'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError(xhr.status || 0, 'unavailable'));
    };
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(part);
  });
}
